<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, Ref, UnwrapRef } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api/index';
import SwrScrollingPagination from '@/components/common/swr-scrolling-pagination.vue';
import GroupComment from '@/components/group/group-comment.vue';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import { Comment, Group } from '@/types';
import {
  STATES,
  useComments,
  useUserActionCount,
  useUserOrganizationTags,
} from '@/utils/swr';
import { getTabs } from '@/utils/user';
import { userSession } from '@/utils/userSession';
import { useEmitter, useStore } from '@/utils/vue';

const pageLimit = 10;

const store = useStore();
const emitter = useEmitter();
const route = useRoute();

const userId = computed(() => userSession.getUserId() ?? undefined);
const { data: userOrganizationTags } = useUserOrganizationTags(userId);

const {
  data: userActionCount,
  state: actionCountState,
  mutate: mutateCount,
} = useUserActionCount(userId, 'comment');

const commentCount = computed(() => userActionCount.value?.count ?? 0);

const userInfo = computed(() => store.state.userInfo.userInfo);
const groups = computed<Group[]>(() => store.state.groups.groups);

const hasNoComments = computed(
  () => actionCountState.value === STATES.SUCCESS && commentCount.value === 0,
);

const tabs = getTabs();

const deleteComment = (comments: Comment[], id: Comment['id']) => {
  const parentIndex = comments.findIndex(c => c.id === id);
  if (parentIndex >= 0) {
    comments.splice(parentIndex, 1);
  } else {
    comments.forEach(comment => {
      const idx = comment.children?.findIndex(child => {
        return child.id === id;
      });
      if (idx >= 0) {
        comment.children.splice(idx, 1);
      }
    });
  }
};

const handleDelete = (id: Comment['id']) => {
  emitter.emit('pagination-item-delete', (items: Comment[]) =>
    deleteComment(items, id),
  );
  mutateCount();
};

const commentFilter = (comment: Comment, id: Comment['id']) => {
  return (
    comment.id === id ||
    comment.children?.some(child => {
      return child.id === id;
    })
  );
};

const updateComments = (comments: Comment[], updatedComment: Comment) => {
  const index = comments.findIndex(c => {
    return c.id === updatedComment.id;
  });
  if (index >= 0) {
    comments[index].content = updatedComment.content;
  } else {
    comments.forEach(c => {
      const idx = c.children?.findIndex(
        child => child.id === updatedComment.id,
      );
      if (idx >= 0) {
        c.children[idx].content = updatedComment.content;
      }
    });
  }
};

const handleUpdate = (comment: Comment) => {
  emitter.emit('pagination-items-update', {
    filterFunc: (i: Comment) => commentFilter(i, comment.id),
    updateFunc: (items: Comment[]) => updateComments(items, comment),
  });
};

onMounted(async () => {
  emitter.on('comment-deleted', handleDelete);
  emitter.on('comment-updated', handleUpdate);

  await trackPageView();
});

onBeforeUnmount(() => {
  emitter.off('comment-deleted', handleDelete);
  emitter.off('comment-updated', handleUpdate);
});

const groupName = (groupId: Comment['group_id']) => {
  return groups.value?.find(g => g.id === groupId)?.name;
};

const trackPageView = async (): Promise<void> => {
  await api.trackPageView({
    pageName: 'user',
    pageUrl: route.fullPath,
  });
};

const paginationFunc = (pageRef: Ref<number>, pageLimit: number) =>
  useComments(pageRef, pageLimit);
const dataAccessor = (d: UnwrapRef<ReturnType<typeof useComments>['data']>) =>
  d?.user_comments;
</script>

<template>
  <div v-if="userInfo" class="o-my-activity-feed">
    <Header
      :title="userInfo.user_name"
      :avatar-image-url="userInfo.image_url"
      :tabs="tabs"
      :organization-tag-list="userOrganizationTags?.organization_tags"
    ></Header>
    <Content>
      <div v-if="actionCountState === STATES.SUCCESS" class="o-feed">
        <div class="o-caption c-title-bold--m">
          コメント<span class="c-text- c-text-regular--s"
            >・{{ commentCount }} 件</span
          >
        </div>
        <SwrScrollingPagination
          v-if="!hasNoComments"
          :page-limit="pageLimit"
          :pagination-func="paginationFunc"
          :data-accessor="dataAccessor"
        >
          <template v-slot="{ items, loaded }">
            <template v-if="loaded">
              <div
                class="o-article-card"
                v-for="comment in items"
                :key="comment.id"
              >
                <GroupComment
                  :comment="comment"
                  :article="comment.article"
                  :group-id="comment.group_id"
                  :group-name="groupName(comment.group_id)"
                  :show-group="true"
                  page-name="user"
                  :feature="'my_comments'"
                />
              </div>
            </template>
          </template>
        </SwrScrollingPagination>
        <div v-else class="o-no-user-comments">
          <div class="o-description c-text-regular--m">
            該当するコメントはありませんでした。
          </div>
          <img src="@/assets/state-empty-comment.png" />
        </div>
      </div>
    </Content>
  </div>
</template>

<style lang="scss" scoped>
.o-my-activity-feed {
  width: 100%;

  .o-feed {
    margin-top: 0px;
    width: 616px;

    .o-caption {
      margin-bottom: 16px;

      span {
        color: $color-gray600;
      }
    }
  }

  .o-no-user-comments {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 616px;
    height: 212px;
    background: #ffffff;
    border: 1px solid $color-gray400;
    box-sizing: border-box;
    border-radius: 4px;
    padding: 0 32px 0 48px;
    img {
      height: 200px;
      width: 200px;
    }
  }
  .o-article-card {
    margin-bottom: 10px;
  }
}

.restrict-disclosure {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 616px;
  height: 212px;
  background: #ffffff;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 0 32px 0 48px;
}
</style>
